import { useState } from 'react';
import { FaStar, FaRegStar } from 'react-icons/fa';
import { formatDateCST, formatTimeCST } from '../../../../../utlis';
import { statusOptions } from '../candidateTrainingUtils';

// Interactive star rating component
const StarRatingInput = ({ value, onChange }) => {
  const [hoverValue, setHoverValue] = useState(0);
  const currentValue = parseInt(value, 10) || 0;

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHoverValue(star)}
          onMouseLeave={() => setHoverValue(0)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          {star <= (hoverValue || currentValue) ? (
            <FaStar className="text-yellow-400" size={24} />
          ) : (
            <FaRegStar className="text-gray-300" size={24} />
          )}
        </button>
      ))}
      {currentValue > 0 && (
        <button
          type="button"
          onClick={() => onChange(0)}
          className="ml-2 text-xs text-gray-500 hover:text-red-500"
        >
          Clear
        </button>
      )}
    </div>
  );
};

const CandidateTrainingsForm = ({
  register,
  errors,
  handleSubmit,
  onSubmit,
  closeModal,
  watch,
  setValue,
  isSubmitting,
  isEditMode,
  defaultValues,
  classes,
  classesLoading,
}) => {
  const watchedStatus = watch('status');
  const watchedRating = watch('rating');

  return (
    <form className="flex flex-col gap-4 md:gap-6 text-text-hint" onSubmit={handleSubmit(onSubmit)}>
      {/* Row 1: Class ID */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5">
        <div className="flex flex-col space-y-1">
          <label htmlFor="class_id" className="text-text-hint mb-1">
            Class <span className="text-danger-500">*</span>
          </label>
          <select
            {...register('class_id', { required: 'Class is required' })}
            id="class_id"
            className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black"
          >
            <option value="">Select a class</option>
            {!classesLoading &&
              classes?.map((cls) => (
                <option key={cls.class_id || cls.id} value={cls.class_id || cls.id}>
                  {cls.description || cls.class_name || cls.name}
                </option>
              ))}
          </select>
          {errors.class_id && (
            <span className="text-red-500 text-xs">{errors.class_id.message}</span>
          )}
        </div>
      </div>

      {/* Row 2: Feedback (full width, expanded) */}
      <div className="flex flex-col space-y-1">
        <label htmlFor="feedback" className="text-text-hint mb-1">
          Feedback
        </label>
        <textarea
          {...register('feedback')}
          placeholder="Enter feedback..."
          id="feedback"
          rows={5}
          className="outline-none border-2 border-secondary-500 rounded-lg px-3 py-2 text-black resize-none w-full"
        />
      </div>

      {/* Row 3: Rating */}
      <div className="flex flex-col space-y-1">
        <label className="text-text-hint mb-1">Rating</label>
        <input type="hidden" {...register('rating')} />
        <StarRatingInput
          value={watchedRating}
          onChange={(value) => setValue('rating', value)}
        />
      </div>

      {/* Row 4: Status */}
      <div className="flex flex-col space-y-1">
        <label className="text-text-hint mb-1">
          Status <span className="text-danger-500">*</span>
        </label>
        <ul className="grid grid-cols-4 w-full max-w-lg">
          {statusOptions.map((option, index) => (
            <li key={option.value} className="relative">
              <input
                {...register('status', { required: 'Status is required' })}
                className="sr-only peer"
                type="radio"
                value={option.value}
                id={`status-${option.value}`}
              />
              <label
                className={`flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:bg-secondary-500 peer-checked:text-white peer-checked:border-secondary-800 text-sm ${
                  index === 0
                    ? 'rounded-tl-full rounded-bl-full'
                    : index === statusOptions.length - 1
                    ? 'rounded-tr-full rounded-br-full'
                    : ''
                }`}
                htmlFor={`status-${option.value}`}
              >
                {option.text}
              </label>
            </li>
          ))}
        </ul>
        {errors.status && (
          <span className="text-red-500 text-xs">{errors.status.message}</span>
        )}
      </div>

      {/* Display-only audit fields for Edit Mode */}
      {isEditMode && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Created By</label>
            <p className="text-sm text-gray-600">{defaultValues?.created_by || 'N/A'}</p>
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Date Created</label>
            <p className="text-sm text-gray-600">
              {defaultValues?.created ? formatDateCST(defaultValues.created) : 'N/A'}
            </p>
            {defaultValues?.created && (
              <p className="text-xs text-gray-500">{formatTimeCST(defaultValues.created)}</p>
            )}
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Updated By</label>
            <p className="text-sm text-gray-600">{defaultValues?.updated_by || 'N/A'}</p>
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Last Updated</label>
            <p className="text-sm text-gray-600">
              {defaultValues?.updated ? formatDateCST(defaultValues.updated) : 'N/A'}
            </p>
            {defaultValues?.updated && (
              <p className="text-xs text-gray-500">{formatTimeCST(defaultValues.updated)}</p>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4">
        <div className="flex flex-row-reverse justify-between items-center gap-3 w-full">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-secondary-700 text-text-light py-2 px-6 rounded-lg shadow hover:bg-secondary-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? isEditMode
                ? 'Updating...'
                : 'Saving...'
              : isEditMode
              ? 'Update'
              : 'Save'}
          </button>
          <button
            type="button"
            onClick={closeModal}
            disabled={isSubmitting}
            className="border border-secondary-800 text-secondary-800 py-2 px-6 rounded-lg hover:bg-gray-100 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

export default CandidateTrainingsForm;
